import { Reference } from '@ninetailed/experience.js';
import { ExperienceConfiguration } from '@ninetailed/experience.js-next';
import { INavigation, INavigationElement } from 'global';
import { parseExperiences } from 'utils/ninetailed';
import { NavigationType, PetType, TargetGroup } from '.';
import { NavigationElementConfig } from '../data';
import { ElementsByConfig } from '../use-navigation-data';

/**
 * Helper function to find navigation element
 */
export const findNavigationElement = (
  elements: INavigationElement[] | undefined,
  config: NavigationElementConfig
): INavigationElement | null => {
  if (!elements) return null;

  return (
    elements.find(
      (element) =>
        element.fields?.type === NavigationType[config.type] &&
        element.fields?.targetGroup === TargetGroup[config.targetGroup] &&
        element.fields?.petType === PetType[config.petType]
    ) || null
  );
};

/**
 * Helper function to create experiment baseline and experiences data
 */
export const createExperimentData = (
  element: INavigationElement | null
): {
  baseline: Reference;
  experiences: ExperienceConfiguration<Reference>[];
} => {
  return {
    baseline: element
      ? ({ id: element.sys.id, ...element } as Reference)
      : (null as unknown as Reference),
    experiences: element ? parseExperiences({ entry: element }) : [],
  };
};

/**
 * Helper function to process variant
 */
export const processVariant = (
  variant: unknown,
  originalElement: INavigationElement
): INavigationElement | null => {
  if (variant && typeof variant === 'object') {
    if ((variant as unknown as INavigationElement).fields) {
      return variant as INavigationElement;
    } else {
      return {
        ...originalElement,
        fields: variant,
      } as INavigationElement;
    }
  }
  return null;
};

/**
 * Helper function to get experiment data by config key
 */
export const getExperimentData = ({
  type,
  petType,
  targetGroup,
  elementsByConfig,
}: {
  type: string;
  petType: string;
  targetGroup: string;
  elementsByConfig: Map<string, ElementsByConfig>;
}): {
  baseline: Reference;
  experiences: ExperienceConfiguration<Reference>[];
} => {
  const key = `${type}_${petType}_${targetGroup}`;
  const data = elementsByConfig.get(key)?.experimentData;
  return {
    baseline: data?.baseline || ({ id: '', sys: { id: '' } } as Reference),
    experiences:
      (data?.experiences as ExperienceConfiguration<Reference>[]) || [],
  };
};

/**
 * Clean navigation data to prevent circular reference errors while preserving A/B testing
 */
export const cleanNavigationForExperiments = (
  nav: INavigation | undefined
): INavigation | undefined => {
  if (!nav) return nav;

  try {
    // Test if it can be serialized first
    JSON.stringify(nav);
    return nav; // No circular references, return as-is
  } catch (error) {
    // Check if the error is due to circular references
    if (
      error instanceof Error &&
      error.message.includes('Converting circular structure to JSON')
    ) {
      console.warn(
        'Circular reference detected in navigation data, applying cleaning while preserving A/B testing'
      );

      try {
        return {
          ...nav,
          sys: nav.sys,
          fields: {
            ...nav.fields,
            // Remove the problematic arrays but keep structure
            nt_variants: [],
            nt_experiences: [],
          },
        } as INavigation;
      } catch (cleanError) {
        // If even cleaning fails, return minimal structure
        console.warn('Advanced cleaning failed, using minimal fallback');
        return {
          sys: { id: nav.sys?.id || '' },
          fields: {},
        } as INavigation;
      }
    }
  }
};
